import { ErrorState } from "@ffn/components"
import { createRouter, type RouterHistory, RouterProvider } from "@tanstack/react-router"
import { routeTree } from "./routeTree.gen"

export function SiteError({ error, reset }: { readonly error: Error; readonly reset: () => void }) {
  return <ErrorState detail={error.message} onRetry={reset} />
}

export function SiteNotFound() {
  return <ErrorState title="Not found" detail="This page does not exist." />
}

export interface MakeRouterOptions {
  /**
   * True when rendering to a string at build time (src/entry-server.tsx). It
   * switches the router to its server rendering path and turns scroll
   * restoration off: on the server that option emits an inline script the
   * client never renders, which would be a hydration mismatch on every page.
   */
  readonly isServer?: boolean
}

/**
 * The router is built here rather than in main.tsx so a test can hand it a memory
 * history and render the same tree the browser gets, and so the prerender can
 * build one per path.
 */
export function makeRouter(history?: RouterHistory, options: MakeRouterOptions = {}) {
  const isServer = options.isServer ?? false
  return createRouter({
    routeTree,
    ...(history === undefined ? {} : { history }),
    isServer,
    defaultPreload: "intent",
    scrollRestoration: !isServer,
    defaultErrorComponent: SiteError,
    defaultNotFoundComponent: SiteNotFound
  })
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>
  }
}

export function App({ history }: { readonly history?: RouterHistory }) {
  return <RouterProvider router={makeRouter(history)} />
}
